/**
 * Stats tracker context — the tracker the SMSWiser stats tabs below it read.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The country stats page wraps its tabs in a provider holding the active
 * group's tracker; the stats hooks add `useStatsTrackerParams()` to their query
 * params (GET) or body (phone POSTs). Only Tres Commas names itself
 * (`tracker: 'tres-commas'`): its campaign
 * ids are Voluum's (imported), so the backend must not have to guess. Voluum,
 * Binom and anything outside the provider add NOTHING — their requests, query
 * keys and responses stay exactly what they were.
 */

import { createContext, useContext, type FC, type ReactNode } from 'react';
import { assertNever, trackerFromEnum, trackerLabel, type TrackerEnum } from '@trafficpulse/shared';

export type StatsTrackerParams = Readonly<Record<string, string>>;

const NO_TRACKER_PARAM: StatsTrackerParams = Object.freeze({});
const TRES_COMMAS_PARAM: StatsTrackerParams = Object.freeze({ tracker: 'tres-commas' });

/** The query params a stats request adds for this tracker (none for Voluum/Binom). */
export function statsTrackerParams(provider: TrackerEnum | null): StatsTrackerParams {
  switch (provider) {
    case null:
    case 'VOLUUM':
    case 'BINOM':
      return NO_TRACKER_PARAM;
    case 'TRES_COMMAS':
      return TRES_COMMAS_PARAM;
    default:
      return assertNever(provider, 'traffic provider');
  }
}

const StatsTrackerContext = createContext<TrackerEnum | null>(null);

export const StatsTrackerProvider: FC<{ provider: TrackerEnum; children: ReactNode }> = ({
  provider,
  children,
}) => <StatsTrackerContext.Provider value={provider}>{children}</StatsTrackerContext.Provider>;

export function useStatsTrackerParams(): StatsTrackerParams {
  return statsTrackerParams(useContext(StatsTrackerContext));
}

/** The active group's tracker by name, for copy ("Voluum", "Tres Commas"); `fallback` outside a provider. */
export function useStatsTrackerName(fallback = 'tracker'): string {
  const provider = useContext(StatsTrackerContext);
  return provider ? trackerLabel(trackerFromEnum(provider)) : fallback;
}
